<script setup lang="ts">
  import { computed, watch } from 'vue';

  import type { Contact } from '@stuut/api/models';
  import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@ui/components/dialog';

  import ContactEditor from './ContactEditor.vue';
  import type { ContactEditorProps } from './ContactEditor.vue';

  const props = defineProps<
    ContactEditorProps & {
      createWithPartner?: boolean;
      showPartnerSelect?: boolean;
      disablePartnerSelect?: boolean;
    }
  >();

  const emits = defineEmits<{
    (e: 'saved', contact: Contact): void;
    (e: 'close'): void;
  }>();

  const isOpen = defineModel<boolean>();

  const handleClose = () => {
    isOpen.value = false;
  };

  watch(isOpen, () => {
    if (!isOpen.value) emits('close');
  });

  const title = computed(() => {
    const action = props.contact?.id ? 'Update' : 'Create';
    const type = props.mode === 'internal' ? 'Internal' : 'Customer';
    return `${action} ${type} Contact`;
  });
</script>

<template>
  <Dialog v-model:open="isOpen">
    <DialogContent
      class="max-w-[450px] w-full h-fit overflow-y-auto p-6"
      :disable-dismiss-on="['esc', 'interact-outside']"
      :on-escape-key-down="handleClose"
      @pointer-down-outside="handleClose"
    >
      <DialogHeader>
        <DialogTitle>{{ title }}</DialogTitle>
      </DialogHeader>

      <ContactEditor
        v-if="isOpen"
        v-bind="props"
        :create-with-partner="props.createWithPartner"
        :disable-partner-select="props.disablePartnerSelect"
        :show-partner-select="props.showPartnerSelect"
        @saved="(contact) => emits('saved', contact)"
        @cancel="handleClose"
      />
    </DialogContent>
  </Dialog>
</template>

<style scoped></style>
